import { Autocomplete, TextField, Typography } from '@mui/material';
import { PATIENT_INITIALS_FEATURE_FLAG } from 'featureFlags';
import { useCallback, useMemo } from 'react';
import { useSearchOrders } from 'api/hooks/useSearchOrders';
import { Delivery } from 'api/models/Deliveries';
import { OrdersSortField, OrderSummary } from 'api/models/Orders';
import { SortOrder } from 'api/models/Sorting';
import theme from 'App.theme';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import { NamedSelectOption } from 'components/DropDown/ValidatingSelect';
import { useLanguage } from 'context/AppContext';
import { useIsHospitalViewer } from 'hooks/useIsHospitalViewer';
import { replaceTimeInfoWithUtcInDate } from 'utils/helpers/DateTimeFormat.helpers';
import PatientInitialsInput from '../../PatientInitialsInput/PatientInitialsInput';
import { useReceiveDeliveryFields } from '../hooks/useReceiveDeliveryFields';

type Props = {
  delivery?: Delivery;
  surgeonOptions: NamedSelectOption[];
  supplierOptions: NamedSelectOption[];
  haveSupplier: boolean;
  isActiveSupplier?: boolean;
  onContinue: (orders: OrderSummary[]) => void;
};

const OrdersSearch = ({
  delivery,
  surgeonOptions,
  supplierOptions,
  haveSupplier,
  isActiveSupplier,
  onContinue,
}: Props) => {
  const lang = useLanguage()[0].ReceiveDeliveryDialog;
  const { fields, setField } = useReceiveDeliveryFields();
  const patientInitialsError = useLanguage()[0].PatientInitialsValidationError.error;

  const { isHospitalViewer } = useIsHospitalViewer();
  const disableEdit = isHospitalViewer || Boolean(delivery);
  const disableSurgeonEdit = disableEdit && Boolean(delivery?.surgery.surgeonId);
  const disableSupplierEdit = disableEdit || haveSupplier;

  const surgeryDateStr = useMemo(
    () => (fields.surgeryDate ? replaceTimeInfoWithUtcInDate(fields.surgeryDate).split('T')[0] : ''),
    [fields.surgeryDate],
  );

  const { refetch: searchOrders, isLoading: searchOrdersLoading } = useSearchOrders({
    offset: '0',
    limit: '1000',
    sort_order: SortOrder.Ascending,
    sort_by: OrdersSortField.SurgeryDate,
    tenant_id: fields.supplier?.id || '',
    surgeon_name: fields.surgeon?.name || '',
    surgery_date: surgeryDateStr,
    patient_initials: isActiveSupplier ? '' : fields.patientInitials,
  });

  const handleContinue = useCallback(async () => {
    // if delivery is already selected, don't search for orders
    if (delivery) {
      onContinue([]);
      return;
    }

    const { data: orders } = await searchOrders();
    onContinue(orders);
  }, [delivery, onContinue, searchOrders]);

  return (
    <div
      style={{
        marginTop: theme.layout.spacing.tiny,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.layout.spacing.medium,
      }}
    >
      <Typography variant="caption" color="error">
        {lang.helperText}
      </Typography>

      <DatePicker
        label={lang.formLabels.receivedDate}
        selected={fields.receivedDate}
        required
        disabled={isHospitalViewer}
        modalised
        size="small"
        onSelect={val => setField('receivedDate', val)}
      />

      <Autocomplete
        value={fields.supplier || null}
        options={supplierOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={disableSupplierEdit}
        size="small"
        renderInput={params => (
          <TextField
            {...params}
            name="supplier"
            label={lang.formLabels.supplier}
            required
            error={!disableSupplierEdit && !fields.supplier}
          />
        )}
        onChange={(_, val) => {
          setField('supplier', val || undefined);
        }}
      />

      <Autocomplete
        value={fields.surgeon || null}
        options={surgeonOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={disableSurgeonEdit}
        size="small"
        renderInput={params => (
          <TextField
            {...params}
            name="surgeon"
            label={lang.formLabels.surgeon}
            required
            error={!disableSurgeonEdit && !fields.surgeon}
          />
        )}
        onChange={(_, val) => {
          setField('surgeon', val || undefined);
        }}
      />

      <DatePicker
        label={lang.formLabels.surgeryDate}
        selected={fields.surgeryDate}
        required
        disabled={disableEdit}
        modalised
        size="small"
        onSelect={val => setField('surgeryDate', val)}
      />
      {PATIENT_INITIALS_FEATURE_FLAG && (
        <PatientInitialsInput
          value={fields.patientInitials}
          onChange={value => setField('patientInitials', value)}
          disabled={Boolean(delivery?.patientInitials)}
          required
          placeholder="Enter 'TBC' if patient unknown"
          errorMessage={patientInitialsError}
          size="small"
        />
      )}

      <Button
        label={lang.continue}
        colors="white on active"
        onClick={handleContinue}
        disabled={
          searchOrdersLoading ||
          isHospitalViewer ||
          !fields.receivedDate ||
          !fields.supplier ||
          !fields.surgeon ||
          !fields.surgeryDate ||
          (PATIENT_INITIALS_FEATURE_FLAG && !fields.patientInitials)
        }
        loading={searchOrdersLoading}
        sx={{ height: '40px' }}
      />
    </div>
  );
};

export default OrdersSearch;
